/**
 * 从相册移除（WIRE-01 A7，总账 N-05；文案 I 10-10 定稿）：POST /pets/{pet_id}/album/{photo_id}/hide（CSRF＋Idempotency-Key，没有请求体）。
 * 后端只记一笔「不在相册显示」（web_album_hidden），不删照片、不动私聊与收藏；契约 AlbumHideResult 也这样写。
 * - 入口在照片详情页底部：一颗透明按钮「从相册移除」（不是主按钮）。点了先弹底部面板轻确认：
 *   标题「从相册移除这张照片？」，正文「只是不在相册里显示，照片本身不会删除，聊天和收藏里的照样在。」，主「移除」、次「先不了」。
 * - 成功：回到相册（替换掉详情这一页的历史，返回不会回到移除了的那张），相册顶上一句「已从相册移除」，礼貌播报，1.2 秒后收起；
 *   这张从相册的缓存里拿掉、相册重读，不再出现。
 * - 失败：面板不关，下面一句「这次没移除，稍后再试。」；再点「移除」是同一个幂等键、同一个请求（回执丢了重试也只记一笔）。
 *   409 先认 details.original：那次其实已经移除了，当成功。
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQueryClient, type InfiniteData, type Query, type QueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import type { AlbumHideResult, PetAlbumPage } from "@/shared/contracts";
import { isApiError } from "@/shared/api/errors";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { useServices } from "@/shared/services/registry";
import { Button, Sheet } from "@/shared/ui";
import { env } from "@/shared/config/env";
import { albumBookKey, albumPhotoKey } from "./album";

export const ALBUM_REMOVED = "已从相册移除";
/** 「已从相册移除」停多久再收起（I 10-10：1.2 秒）。 */
export const ALBUM_REMOVED_HOLD_MS = 1200;

type Book = InfiniteData<PetAlbumPage, string | null>;
type Sent = { petId: string; photoId: string; key: string };

/** 409 带着 original，而且就是这一张、已经移除：那次其实成了。 */
export function hiddenByOriginal(error: unknown, photoId: string): boolean {
  if (!isApiError(error) || error.status !== 409) return false;
  const original = error.details?.original as Partial<AlbumHideResult> | null | undefined;
  return Boolean(original && typeof original === "object" && original.photo_id === photoId && original.hidden === true);
}

/** 从一叠相册缓存里拿掉这一张；拿掉了就把「一共几张」减一。 */
function without(book: Book | undefined, photoId: string): Book | undefined {
  if (!book || !book.pages.some((page) => page.items.some((item) => item.photo_id === photoId))) return book;
  return {
    ...book,
    pages: book.pages.map((page) => ({ ...page, items: page.items.filter((item) => item.photo_id !== photoId), total_visible: Math.max(0, page.total_visible - 1) })),
  };
}

/** 相册各处的读数（相册两叠、照片详情、回忆页张数、小窝里的相册）：都以 ["pets", "album…"] 开头。 */
const isAlbumQuery = (query: Query) => query.queryKey[0] === "pets" && typeof query.queryKey[1] === "string" && query.queryKey[1].startsWith("album");

type HideOwner = {
  userId: string | null; petId: string; photoId: string; mode: string;
  pets: ReturnType<typeof useServices>["pets"]; client: QueryClient; epoch: number;
};
let nextHideEpoch = 0;
function newHideOwner(userId: string | null, petId: string, photoId: string, mode: string, pets: HideOwner["pets"], client: QueryClient): HideOwner {
  return { userId, petId, photoId, mode, pets, client, epoch: ++nextHideEpoch };
}

export function AlbumHide({ petId, userId, photoId }: { petId: string; userId: string | null; photoId: string }) {
  const { pets } = useServices();
  const client = useQueryClient();
  const [scope, setScope] = useState(() => newHideOwner(userId, petId, photoId, env.dataMode, pets, client));
  const committed = useRef<HideOwner | null>(null);
  useLayoutEffect(() => {
    committed.current = scope;
    return () => { if (committed.current === scope) committed.current = null; };
  }, [scope]);
  if (scope.userId !== userId || scope.petId !== petId || scope.photoId !== photoId || scope.mode !== env.dataMode || scope.pets !== pets || scope.client !== client) {
    setScope(newHideOwner(userId, petId, photoId, env.dataMode, pets, client));
    return null;
  }
  if (env.dataMode === "live" && (!userId?.trim() || !petId.trim() || !photoId.trim())) return null;
  return <OwnedAlbumHide key={scope.epoch} scope={scope} isCurrent={() => committed.current === scope} />;
}

function OwnedAlbumHide({ scope, isCurrent }: { scope: HideOwner; isCurrent: () => boolean }) {
  const { petId, userId, photoId } = scope;
  const services = { pets: scope.pets };
  const queryClient = scope.client;
  const lifetime = useRef({ active: false, generation: 0 });
  useLayoutEffect(() => {
    const mount = lifetime.current;
    mount.active = true;
    mount.generation++;
    return () => { mount.active = false; mount.generation++; };
  }, [scope]);
  const owns = (generation = lifetime.current.generation) => lifetime.current.active && lifetime.current.generation === generation && isCurrent();
  const busyRef = useRef(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  /** 第一次发出去的那一个请求：没成功之前，再点「移除」原样重发（同一个键）。 */
  const sent = useRef<Sent | null>(null);

  const removed = (generation: number) => {
    if (!owns(generation)) return;
    const user = userId ?? "-";
    for (const category of ["default", "fantasy"] as const) queryClient.setQueryData<Book>(albumBookKey(user, petId, category), (book) => without(book, photoId));
    queryClient.removeQueries({ queryKey: albumPhotoKey(user, petId, photoId), exact: true });
    void queryClient.invalidateQueries({ predicate: isAlbumQuery });
    navigate("/album", { replace: true, state: { albumRemoved: photoId } });
  };

  const remove = async () => {
    const generation = lifetime.current.generation;
    if (busyRef.current || !owns(generation)) return;
    busyRef.current = true;
    const last = sent.current;
    const req: Sent = last && last.petId === petId && last.photoId === photoId ? last : { petId, photoId, key: newIdempotencyKey("album-hide") };
    sent.current = req;
    setBusy(true);
    setFailed(false);
    try {
      await services.pets.hideAlbumPhoto(req.petId, req.photoId, req.key);
      if (!owns(generation)) return;
    } catch (error) {
      if (!owns(generation)) return;
      if (!hiddenByOriginal(error, photoId)) {
        busyRef.current = false;
        setBusy(false);
        setFailed(true);
        return;
      }
    }
    if (!owns(generation)) return;
    sent.current = null;
    removed(generation);
  };

  return (
    <>
      <p className="ps-album-detail__hide">
        <Button variant="ghost" onClick={() => { setFailed(false); setOpen(true); }}>
          从相册移除
        </Button>
      </p>
      {open ? (
        <Sheet title="从相册移除这张照片？" onClose={() => { if (!busyRef.current) setOpen(false); }} className="ps-album-hide-sheet">
          <p className="ps-album-hide-sheet__text">只是不在相册里显示，照片本身不会删除，聊天和收藏里的照样在。</p>
          {failed ? (
            <p className="ps-form-error" role="alert">
              这次没移除，稍后再试。
            </p>
          ) : null}
          <div className="ps-album-hide-sheet__actions">
            <Button variant="secondary" disabled={busy} onClick={() => { if (!busyRef.current) setOpen(false); }}>
              先不了
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void remove()}>
              移除
            </Button>
          </div>
        </Sheet>
      ) : null}
    </>
  );
}

/**
 * 相册顶上那一句「已从相册移除」：从详情页移除成功、带着路由状态回来时出现。播报区一直在（出字前就在，读屏才会念），
 * 礼貌播报，1.2 秒后收起；路由状态当场清掉，刷新、返回都不会再说一遍。没有动画。
 */
export function AlbumRemovedNotice() {
  const location = useLocation();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const removed = (location.state as { albumRemoved?: unknown } | null)?.albumRemoved;
  useEffect(() => {
    if (typeof removed !== "string") return;
    setText(ALBUM_REMOVED);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [removed]);
  useEffect(() => {
    if (!text) return;
    const timer = window.setTimeout(() => setText(""), ALBUM_REMOVED_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [text]);
  return (
    <p className={text ? "ps-album-notice" : "visually-hidden"} aria-live="polite" aria-atomic="true" data-testid="album-live">
      {text}
    </p>
  );
}
